import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGridPremium, type GridColDef, type GridPaginationModel, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import { format, subDays } from 'date-fns';

import DateRangePicker from '../components/DateRangePicker';
import { getConversions, getAllConversions } from '../services/conversions.service';
import type { ConversionRow } from '../types';

const statusColorMap: Record<string, 'success' | 'warning' | 'error'> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'error',
};

const columns: GridColDef[] = [
  {
    field: 'converted_at',
    headerName: 'Date/Time',
    width: 180,
    valueFormatter: (value: string) =>
      value ? new Date(value).toLocaleString() : '-',
  },
  {
    field: 'click_id',
    headerName: 'Click ID',
    width: 220,
    renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {params.value || '-'}
      </Box>
    ),
  },
  {
    field: 'country',
    headerName: 'Country',
    width: 100,
    valueFormatter: (value: string | null) => value || '-',
  },
  {
    field: 'channel',
    headerName: 'Channel',
    width: 140,
    valueFormatter: (value: string | null) => value || '-',
  },
  {
    field: 'event_type',
    headerName: 'Event',
    width: 120,
    valueFormatter: (value: string) => value || '-',
  },
  {
    field: 'payout',
    headerName: 'Payout',
    width: 120,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ color: 'success.main', fontWeight: 600, display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-end', width: '100%' }}>
        ${Number(params.value || 0).toFixed(2)}
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (params: GridRenderCellParams) => {
      const status = (params.value as string) || '';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={status}
            size="small"
            color={statusColorMap[status] || 'default'}
            sx={{ fontWeight: 500, minWidth: 72 }}
          />
        </Box>
      );
    },
  },
  {
    field: 'transaction_id',
    headerName: 'Transaction ID',
    width: 200,
    valueFormatter: (value: string | null) => value || '-',
  },
  {
    field: 'external_id',
    headerName: 'External ID',
    width: 160,
    valueFormatter: (value: string | null) => value || '-',
  },
];

function escapeCsv(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return `"${val.replace(/"/g, '""')}"`;
  }
  return val;
}

async function exportCSV(from: string, to: string) {
  const all = await getAllConversions(from, to);
  const header = ['Date/Time', 'Click ID', 'Country', 'Channel', 'Event', 'Payout', 'Status', 'Transaction ID', 'External ID'];
  const csvRows = all.map((r) => [
    r.converted_at ? new Date(r.converted_at).toLocaleString() : '',
    r.click_id || '',
    r.country || '',
    r.channel || '',
    r.event_type || '',
    Number(r.payout || 0).toFixed(2),
    r.status || '',
    r.transaction_id || '',
    r.external_id || '',
  ].map(escapeCsv).join(','));

  const csv = [header.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `conversions-${from}-to-${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ConversionsPage() {
  const [from, setFrom] = useState(() => format(subDays(new Date(), 6), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const [rows, setRows] = useState<ConversionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleDateChange = useCallback((newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    getConversions(
      from,
      to,
      paginationModel.pageSize,
      paginationModel.page * paginationModel.pageSize,
    )
      .then((result) => {
        if (cancelled) return;
        setRows(result.items);
        setTotal(result.total);
      })
      .catch((err) => {
        console.error('Failed to load conversions:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [from, to, paginationModel]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4">Conversions</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <DateRangePicker from={from} to={to} onChange={handleDateChange} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            disabled={total === 0 || exporting}
            onClick={async () => {
              setExporting(true);
              try { await exportCSV(from, to); } finally { setExporting(false); }
            }}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row: ConversionRow) => row.id}
        loading={loading}
        density="compact"
        pagination
        paginationMode="server"
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          height: 'calc(100vh - 240px)',
          minHeight: 400,
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(255,255,255,0.04)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(108, 142, 255, 0.04)',
          },
        }}
      />
    </Box>
  );
}
